import * as THREE from "three";
import { ENVIRONMENT } from "./createEnvironment";

type SwitchMaterialsParams = {
  model: THREE.Group;
  // Name of a material authored inside the GLB (e.g. "drevo1").
  materialName: string;
  // Exact model node names that should receive the material
  // (e.g. ["sedak", "operadlo"]).
  applyTo: string[];
};

const normalize = (name: string): string => (name || "").toLowerCase().trim();

function isStandardLike(
  material: THREE.Material,
): material is THREE.MeshStandardMaterial | THREE.MeshPhysicalMaterial {
  return (
    material instanceof THREE.MeshStandardMaterial ||
    material instanceof THREE.MeshPhysicalMaterial
  );
}

/**
 * Pins the swapped material to a matte, non-metallic surface.
 *
 * The GLB packs each part's "reflexni" V-Ray glossiness map into the glTF
 * metallic-roughness slot. Under this scene's bright IBL it reads as a near
 * mirror (the "drevo1" legs rendered as chrome). Dropping that map and forcing
 * a fully rough, non-metallic surface keeps every variant reading as the
 * intended wood/fabric, while the authored base colour and normal detail stay.
 */
function normalizeSurface(
  material: THREE.MeshStandardMaterial | THREE.MeshPhysicalMaterial,
): void {
  material.metalness = 0;
  material.roughness = 1;
  material.metalnessMap = null;
  material.roughnessMap = null;
  material.envMapIntensity = ENVIRONMENT.MAP_INTENSITY;
  material.needsUpdate = true;
}

/**
 * Swaps built-in GLB materials onto the named nodes.
 *
 * Unlike `applyTexture`, this assigns materials that were already authored in
 * the model (collected at load time from the `KHR_materials_variants` set into
 * `model.userData.builtInMaterials`) instead of loading external textures.
 *
 * Node matching is exact (case-insensitive) so that, for example, applying a
 * fabric to "sedak" does not also hit the wooden "sedak-spodek".
 */
export function switchMaterials(params: SwitchMaterialsParams): void {
  const { model, materialName, applyTo } = params;
  if (!model || !materialName || !applyTo.length) return;

  const builtInMaterials = model.userData.builtInMaterials as
    | Record<string, THREE.Material>
    | undefined;
  const material = builtInMaterials?.[materialName];
  if (!material) {
    console.warn(
      `switchMaterials: built-in material "${materialName}" not found in model`,
    );
    return;
  }
  if (isStandardLike(material)) normalizeSurface(material);

  const targets = new Set(applyTo.map(normalize));
  model.traverse((child) => {
    if (child instanceof THREE.Mesh && targets.has(normalize(child.name))) {
      child.material = material;
    }
  });
}
